import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Search, Package, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import Spinner from "../components/Spinner";
import RichTextRenderer from "../components/RichTextRenderer";
import SEOHead from "../components/SEOHead";
import { useSettings } from "../context/SettingsContext";
import { getPublicPath, getSeoPage } from "../utils/seoPages";

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all"); // all | packs | courses
  const [refCode] = useState(localStorage.getItem("sf_ref") || "");
  const { settings: s } = useSettings();
  const seo = getSeoPage(s, "/catalog");
  const packsPath = getPublicPath(s, "/packs");

  useEffect(() => {
    Promise.all([
      api.get("/courses").then((r) => setCourses(r.data)),
      api.get("/packs").then((r) => setPacks(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const matchesQ = (text) => `${text}`.toLowerCase().includes(q.toLowerCase());
  const filteredCourses = courses.filter((c) => matchesQ(`${c.title} ${c.short_description} ${c.category || ""}`));
  const filteredPacks = packs.filter((p) => matchesQ(`${p.title} ${p.short_description}`));

  const showPacks = tab !== "courses";
  const showCourses = tab !== "packs";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="catalog-page">
      <SEOHead title={seo.title} description={seo.description} url={typeof window !== "undefined" ? window.location.href : ""} type="website" />
      <div className="mb-10">
        <p className="label">Catalogue complet</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2">Toutes les <span className="text-brand-green">formations & packs</span></h1>
        <p className="text-gray-600 mt-3 max-w-2xl">Choisis le programme qui te correspond, ou maximise tes économies avec un pack.</p>
        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <div className="relative max-w-md flex-1 min-w-[260px]">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher..."
              className="input pl-11"
              data-testid="catalog-search-input"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: "all", label: "Tout" },
              { key: "packs", label: "Packs" },
              { key: "courses", label: "Formations" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition ${tab === t.key ? "bg-brand-green text-white" : "bg-white border border-gray-200 hover:border-brand-green"}`}
                data-testid={`catalog-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {showPacks && filteredPacks.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-5">
                <Package className="w-5 h-5 text-brand-orange" />
                <h2 className="font-heading text-2xl font-bold">Packs <span className="badge orange ml-2"><Sparkles className="w-3 h-3" /> Économise jusqu'à 30%</span></h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredPacks.map((p) => {
                  const packSlug = p.slug || p.pack_id;
                  const link = refCode ? `${packsPath}/${packSlug}?ref=${refCode}` : `${packsPath}/${packSlug}`;
                  const savings = (p.original_total || 0) - p.price_eur;
                  const savingsPct = p.original_total > 0 ? Math.round((savings / p.original_total) * 100) : 0;
                  return (
                    <motion.div key={p.pack_id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card overflow-hidden group relative" data-testid={`catalog-pack-${p.pack_id}`}>
                      {p.badge && <span className="absolute top-4 left-4 z-10 badge orange shadow-md">{p.badge}</span>}
                      <Link to={link}>
                        <div className="bg-white overflow-hidden">
                          {p.cover_image && <img src={p.cover_image} alt={p.seo_subtitle || p.title || "Pack SF Learning"} className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700" />}
                        </div>
                      </Link>
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="badge"><Package className="w-3 h-3" /> {(p.courses || []).length} formations</span>
                          {savings > 0 && <span className="badge orange">-{savingsPct}%</span>}
                        </div>
                        <Link to={link}>
                          <h3 className="font-heading text-2xl font-bold mb-2 group-hover:text-brand-green">{p.title}</h3>
                        </Link>
                        <RichTextRenderer html={p.short_description} className="text-sm text-gray-600 line-clamp-2" />
                        <div className="mt-5 flex items-center justify-between">
                          <div>
                            {savings > 0 && <span className="text-xs text-gray-400 line-through">{p.original_total.toFixed(0)}€</span>}
                            <p className="text-3xl font-heading font-bold text-brand-green">{p.price_eur}€</p>
                          </div>
                          <Link to={link} className="btn-primary !px-4 !py-2 text-sm" data-testid={`catalog-pack-cta-${p.pack_id}`}>
                            Voir le pack <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          )}

          {showCourses && (
            <section>
              {showPacks && filteredPacks.length > 0 && (
                <div className="flex items-center gap-2 mb-5">
                  <h2 className="font-heading text-2xl font-bold">Formations individuelles</h2>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map((c) => {
                  const link = refCode ? `/formations/${c.slug || c.course_id}?ref=${refCode}` : `/formations/${c.slug || c.course_id}`;
                  const totalLessons = (c.modules || []).reduce((s, m) => s + (m.lessons || []).length, 0);
                  return (
                    <motion.div key={c.course_id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card overflow-hidden group" data-testid={`catalog-course-${c.course_id}`}>
                      <Link to={link} className="block">
                        <div className="aspect-[16/10] bg-emerald-50 overflow-hidden">
                          {c.cover_image ? <img src={c.cover_image} alt={c.seo_subtitle || c.title || "Formation SF Learning"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : null}
                        </div>
                      </Link>
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          {c.category && <span className="badge orange">{c.category}</span>}
                          <span className="badge gray">{totalLessons} leçons</span>
                        </div>
                        <Link to={link}>
                          <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-brand-green">{c.title}</h3>
                        </Link>
                        <RichTextRenderer html={c.short_description} className="text-sm text-gray-600 line-clamp-3" />
                        <div className="mt-5 flex items-center justify-between">
                          <p className="text-2xl font-heading font-bold text-brand-green">{c.price_eur}€</p>
                          <Link to={link} className="btn-primary !px-4 !py-2 text-sm" data-testid={`catalog-cta-${c.course_id}`}>
                            Découvrir <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          )}

          {filteredCourses.length === 0 && filteredPacks.length === 0 && (
            <p className="text-center text-gray-500 mt-16" data-testid="catalog-empty">Aucun résultat trouvé.</p>
          )}
        </>
      )}
    </div>
  );
}
